import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Col, Row, Form, ButtonGroup, ToggleButton, Overlay, Popover, Button } from "react-bootstrap";
import DatePicker from "react-datepicker";

import { withNavigation } from "../../../../utils/wrapper";
import Address from "../../../../layout/address";
import CodeSelect from "../../../../layout/select/code";
import StateSelect from "../../../../layout/select/state";
import GenerationsSelect from "../../../../layout/select/generation";
import { PhoneInput } from "react-international-phone";

function getByPath(obj, path) {
  if (!path) return undefined;
  return String(path)
    .split(".")
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

function normalizeComparable(value) {
  if (value == null) return "";
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") return JSON.stringify(value);
  return String(value).trim();
}

function toDisplayDate(value) {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function formatSuggestionValue(value, path = "") {
  if (value == null || value === "") return "[blank]";

  const lowerPath = String(path || "").toLowerCase();

  if (lowerPath.includes("date")) {
    return toDisplayDate(value) || String(value);
  }

  if (lowerPath.includes("phone")) {
    return String(value);
  }

  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function Party(props) {
  const {
    uuid,
    caption = "Party",
    Person,
    EmployerAddress,
    restore,
    maiden,
    driversLicenseNumber,
    driversLicenseState,
    pregnant,
    dueDate,
    marriages,
    education,
    military,
    title,
    employerName,
    employerPhone,
    defaultCountry,
    onChange,
    suggestions,
    onApplySuggestion,
    onRejectSuggestion,
  } = props;

  const [uuidState, setUuidState] = useState(uuid ?? null);
  const [personState, setPersonState] = useState(Person ?? {});
  const [employerAddressState, setEmployerAddressState] = useState(EmployerAddress ?? {});
  const [restoreState, setRestoreState] = useState(restore ?? false);
  const [maidenState, setMaidenState] = useState(maiden ?? "");
  const [driversLicenseNumberState, setDriversLicenseNumberState] = useState(driversLicenseNumber ?? "");
  const [driversLicenseStateState, setDriversLicenseStateState] = useState(driversLicenseState ?? "");
  const [educationState, setEducationState] = useState(String(education ?? "0"));
  const [dueDateState, setDueDateState] = useState(dueDate ?? null);
  const [pregnantState, setPregnantState] = useState(pregnant ?? false);
  const [marriagesState, setMarriagesState] = useState(String(marriages ?? "1"));
  const [militaryState, setMilitaryState] = useState(military ?? "unknown");
  const [titleState, setTitleState] = useState(title ?? "spouse");
  const [employerNameState, setEmployerNameState] = useState(employerName ?? "");
  const [employerPhoneState, setEmployerPhoneState] = useState(employerPhone ?? "");

  const syncingFromPropsRef = useRef(false);
  const [suggestionMenu, setSuggestionMenu] = useState({
    show: false,
    target: null,
    path: null,
  });

  const toSSNDigits = useCallback((value) => {
    return String(value ?? "").replace(/\D/g, "").slice(0, 9);
  }, []);

  const toDashedSSN = useCallback(
    (digits) => {
      const d = toSSNDigits(digits);
      const a = d.slice(0, 3);
      const b = d.slice(3, 5);
      const c = d.slice(5, 9);
      if (d.length <= 3) return a;
      if (d.length <= 5) return `${a}-${b}`;
      return `${a}-${b}-${c}`;
    },
    [toSSNDigits]
  );

  const ssnDisplay = useMemo(() => toDashedSSN(personState?.ssn), [personState?.ssn, toDashedSSN]);

  const currentSnapshot = useMemo(() => ({
    uuid: uuidState,
    Person: {
      ...(personState || {}),
      ssn: String(personState?.ssn ?? "").replace(/\D/g, "").slice(0, 9),
    },
    EmployerAddress: employerAddressState,
    restore: restoreState,
    maiden: maidenState,
    pregnant: pregnantState,
    dueDate: dueDateState,
    marriages: Number(marriagesState || 1),
    military: militaryState,
    title: titleState,
    employerName: employerNameState,
    employerPhone: employerPhoneState,
    education: Number(educationState ?? 0),
    driversLicenseNumber: driversLicenseNumberState,
    driversLicenseState: driversLicenseStateState,
  }), [
    uuidState,
    personState,
    employerAddressState,
    restoreState,
    maidenState,
    pregnantState,
    dueDateState,
    marriagesState,
    militaryState,
    titleState,
    employerNameState,
    employerPhoneState,
    educationState,
    driversLicenseNumberState,
    driversLicenseStateState
  ]);

  const updatePerson = useCallback((patch) => {
    setPersonState((prev) => ({
      ...(prev || {}),
      ...(patch || {}),
    }));
  }, []);

  const onSSNChange = useCallback(
    (value) => {
      updatePerson({ ssn: toSSNDigits(value) });
    },
    [toSSNDigits, updatePerson]
  );

  useEffect(() => {
    syncingFromPropsRef.current = true;

    setUuidState(uuid ?? null);
    setPersonState({
      ...(Person || {}),
      ssn: String(Person?.ssn ?? "").replace(/\D/g, "").slice(0, 9),
    });
    setEmployerAddressState(EmployerAddress ?? {});
    setRestoreState(restore ?? false);
    setMaidenState(maiden ?? "");
    setDueDateState(dueDate ?? null);
    setPregnantState(pregnant ?? false);
    setMarriagesState(String(marriages ?? "1"));
    setMilitaryState(military ?? "unknown");
    setTitleState(title ?? "spouse");
    setEmployerNameState(employerName ?? "");
    setEmployerPhoneState(employerPhone ?? "");
    setDriversLicenseNumberState(driversLicenseNumber ?? "");
    setDriversLicenseStateState(driversLicenseState ?? "");
    setEducationState(String(education ?? "0"));

    Promise.resolve().then(() => {
      syncingFromPropsRef.current = false;
    });
  }, [
    uuid,
    Person,
    EmployerAddress,
    restore,
    maiden,
    dueDate,
    pregnant,
    marriages,
    military,
    title,
    employerName,
    employerPhone,
    driversLicenseNumber,
    driversLicenseState,
    education
  ]);

  useEffect(() => {
    if (!onChange) return;
    if (syncingFromPropsRef.current) return;

    onChange(currentSnapshot);
  }, [onChange, currentSnapshot]);

  const titleGroupName = `title-${caption || "party"}`;

  const closeSuggestionMenu = useCallback(() => {
    setSuggestionMenu({
      show: false,
      target: null,
      path: null,
    });
  }, []);

  const openSuggestionMenu = useCallback((event, path) => {
    event.preventDefault();
    event.stopPropagation();
    setSuggestionMenu({
      show: true,
      target: event.currentTarget,
      path,
    });
  }, []);

  const hasSuggestion = useCallback((path) => {
    const incoming = getByPath(suggestions || {}, path);
    if (incoming === undefined) return false;

    const current = getByPath(currentSnapshot, path);
    return normalizeComparable(current) !== normalizeComparable(incoming);
  }, [suggestions, currentSnapshot]);

  const applySuggestion = useCallback(() => {
    const path = suggestionMenu.path;
    if (!path) return;

    const value = getByPath(suggestions || {}, path);
    if (onApplySuggestion) onApplySuggestion(path, value);
    closeSuggestionMenu();
  }, [suggestionMenu.path, suggestions, onApplySuggestion, closeSuggestionMenu]);

  const rejectSuggestion = useCallback(() => {
    const path = suggestionMenu.path;
    if (!path) return;

    const value = getByPath(suggestions || {}, path);
    if (onRejectSuggestion) onRejectSuggestion(path, value);
    closeSuggestionMenu();
  }, [suggestionMenu.path, suggestions, onRejectSuggestion, closeSuggestionMenu]);

  const renderLabel = useCallback((text, path, style = undefined) => {
    const showIcon = hasSuggestion(path);

    return (
      <Form.Label style={style}>
        {text}
        {showIcon ? (
          <button
            type="button"
            onClick={(e) => openSuggestionMenu(e, path)}
            style={{
              border: 0,
              background: "transparent",
              padding: 0,
              marginLeft: 6,
              lineHeight: 1,
              fontSize: "0.95rem",
              cursor: "pointer",
            }}
            title="Review imported survey value"
          >
            ✨
          </button>
        ) : null}
      </Form.Label>
    );
  }, [hasSuggestion, openSuggestionMenu]);

  const activeSuggestionValue = suggestionMenu.path
    ? getByPath(suggestions || {}, suggestionMenu.path)
    : undefined;
  
  const applyNestedSuggestion = useCallback((prefix, path, value) => {
    if (!onApplySuggestion) return;
    onApplySuggestion(`${prefix}.${path}`, value);
  }, [onApplySuggestion]);

  const rejectNestedSuggestion = useCallback((prefix, path, value) => {
    if (!onRejectSuggestion) return;
    onRejectSuggestion(`${prefix}.${path}`, value);
  }, [onRejectSuggestion]);

  return (
    <Col xs="12">
      <Row className="g-3 pb-3">
        <Col lg="12">
          <h2 className="small-title">
            {caption}
            <ButtonGroup className="matter-title-button-group ms-3" aria-label="Title toggle group">
              <ToggleButton
                id={`${titleGroupName}-spouse`}
                type="radio"
                variant="outline-primary"
                name={titleGroupName}
                value="spouse"
                checked={titleState === "spouse"}
                onChange={(e) => setTitleState(e.currentTarget.value)}
              >
                Spouse
              </ToggleButton>

              <ToggleButton
                id={`${titleGroupName}-husband`}
                type="radio"
                variant="outline-primary"
                name={titleGroupName}
                value="husband"
                checked={titleState === "husband"}
                onChange={(e) => setTitleState(e.currentTarget.value)}
              >
                Husband
              </ToggleButton>

              <ToggleButton
                id={`${titleGroupName}-wife`}
                type="radio"
                variant="outline-primary"
                name={titleGroupName}
                value="wife"
                checked={titleState === "wife"}
                onChange={(e) => setTitleState(e.currentTarget.value)}
              >
                Wife
              </ToggleButton>
            </ButtonGroup>
          </h2>
        </Col>
      </Row>

      <Row className="g-3">
        <Col lg="4">
          <div className="mb-3 top-label">
            <Form.Control
              type="text"
              name="firstName"
              value={personState?.firstName ?? ""}
              onChange={(e) => updatePerson({ firstName: e.target.value })}
            />
            {renderLabel("FIRST NAME", "Person.firstName")}
          </div>
        </Col>
        <Col lg="2">
          <div className="mb-3 top-label">
            <Form.Control
              type="text"
              name="middleName"
              value={personState?.middleName ?? ""}
              onChange={(e) => updatePerson({ middleName: e.target.value })}
            />
            {renderLabel("MIDDLE NAME", "Person.middleName")}
          </div>
        </Col>
        <Col lg="4">
          <div className="mb-3 top-label">
            <Form.Control
              type="text"
              name="lastName"
              value={personState?.lastName ?? ""}
              onChange={(e) => updatePerson({ lastName: e.target.value })}
            />
            {renderLabel("LAST NAME", "Person.lastName")}
          </div>
        </Col>
        <Col lg="2">
          <div className="mb-3 top-label">
            <GenerationsSelect
              placeholder=""
              name="generation"
              value={personState?.generation ?? ""}
              onChange={(e) => updatePerson({ generation: e?.value ?? "" })}
            />
            {renderLabel("SUFFIX", "Person.generation")}
          </div>
        </Col>
      </Row>

      <Row className="g-3">
        <Col sm="8" md="10">
          <div className="mb-3 top-label">
            <Form.Control
              type="text"
              name="maiden"
              value={maidenState}
              onChange={(e) => setMaidenState(e.target.value)}
            />
            {renderLabel("MAIDEN NAME", "maiden")}
          </div>
        </Col>
        <Col sm="4" md="2">
          <Form.Check
            type="switch"
            id={`restore-${caption}`}
            label="Restore?"
            checked={restoreState}
            style={{ marginTop: "15px" }}
            onChange={(e) => setRestoreState(e.target.checked)}
          />
        </Col>
      </Row>

      <Row className="g-3">
        <Col md="6" lg="8">
          <div className="mb-3 top-label">
            <Form.Control
              type="text"
              name="email"
              value={personState?.email ?? ""}
              onChange={(e) => updatePerson({ email: e.target.value })}
            />
            {renderLabel("EMAIL ADDRESS", "Person.email")}
          </div>
        </Col>
        <Col md="6" lg="4">
          <div className="phone-bootstrap-wrap top-label">
            <PhoneInput
              name="phone"
              id={`phone-${caption}`}
              defaultCountry={defaultCountry ?? "us"}
              value={personState?.phone ?? ""}
              onChange={(value) => updatePerson({ phone: value ?? "" })}
              inputStyle={{
                width: "100%",
                height: "calc(1.5em + .75rem + 16px)",
                border: "1px solid #ced4da",
                borderRadius: "0 .375rem .375rem 0",
                padding: ".375rem .75rem",
                fontSize: "1rem",
                lineHeight: "1.5",
              }}
              countrySelectorStyleProps={{
                buttonStyle: {
                  border: "1px solid #ced4da",
                  borderRight: 0,
                  borderRadius: ".375rem 0 0 .375rem",
                  backgroundColor: "#fff",
                  height: "calc(1.5em + .75rem + 19px)",
                },
              }}
            />
            {renderLabel(
              "PHONE #",
              "Person.phone",
              { left: "calc(100% - 60px)", position: "absolute", backgroundColor: "transparent" }
            )}
          </div>
        </Col>
      </Row>

      <Row className="g-3">
        <Col md="6" lg="4">
          <div className="mb-3 top-label">
            <Form.Control
              type="text"
              name="ssn"
              autoComplete="off"
              inputMode="numeric"
              placeholder="___-__-____"
              value={ssnDisplay}
              onChange={(e) => onSSNChange(e.target.value)}
            />
            {renderLabel("SOCIAL SECURITY #", "Person.ssn")}
          </div>
        </Col>
        <Col md="6" lg="4">
          <div className="mb-3 top-label">
            <Form.Control
              type="text"
              name="driversLicenseNumber"
              value={driversLicenseNumberState ?? ""}
              onChange={(e) => setDriversLicenseNumberState(e.target.value)}
            />
            {renderLabel("DRIVER'S LICENSE #", "driversLicenseNumber")}
          </div>
        </Col>
        <Col md="6" lg="4">
          <div className="mb-3 top-label">
            <StateSelect
              name="driversLicenseState"
              value={driversLicenseStateState ?? ""}
              onChange={(e) => setDriversLicenseStateState(e ?? "")}
            />
            {renderLabel("ISSUING STATE", "driversLicenseState")}
          </div>
        </Col>
      </Row>

      <Row className="g-3">
        <Col md="4">
          <div className="mb-3 top-label">
            <DatePicker
              isClearable
              id={`birthDate-${caption}`}
              name="birthDate"
              className="form-control"
              selected={personState?.birthDate ? new Date(personState.birthDate) : null}
              onChange={(date) => updatePerson({ birthDate: date })}
            />
            {renderLabel("BIRTHDATE", "Person.birthDate")}
          </div>
        </Col>

        <Col md="4">
          <div className="mb-3 top-label">
            <CodeSelect
              codes={[
                { value: "unknown", description: "Unknown" },
                { value: "female", description: "Female" },
                { value: "male", description: "Male" },
                { value: "other", description: "Other" },
              ]}
              value={personState?.gender ?? "unknown"}
              onChange={(e) => updatePerson({ gender: e?.value ?? "unknown" })}
              placeholder=""
              name="gender"
            />
            {renderLabel("GENDER", "Person.gender")}
          </div>
        </Col>

        <Col md="4">
          <div className="mb-3 top-label">
            <CodeSelect
              codes={[
                { value: "0", description: "Kindergarten (or less)" },
                { value: "1", description: "First Grade" },
                { value: "2", description: "Second Grade" },
                { value: "3", description: "Third Grade" },
                { value: "4", description: "Fourth Grade" },
                { value: "5", description: "Fifth Grade" },
                { value: "6", description: "Sixth Grade" },
                { value: "7", description: "Seventh Grade" },
                { value: "8", description: "Eighth Grade" },
                { value: "9", description: "Ninth Grade" },
                { value: "10", description: "Tenth Grade" },
                { value: "11", description: "Eleventh Grade" },
                { value: "12", description: "Twelfth Grade" },
                { value: "13", description: "College (1 year)" },
                { value: "14", description: "College (2 years)" },
                { value: "15", description: "College (3 years)" },
                { value: "16", description: "Bachelor's Degree" },
                { value: "17", description: "Post-Grad Degree" },
              ]}
              value={String(educationState ?? "0")}
              onChange={(e) => setEducationState(String(e?.value ?? "0"))}
              placeholder=""
              name="education"
            />
            {renderLabel("EDUCATION LEVEL", "education")}
          </div>
        </Col>
      </Row>

      <Row className="g-3">
        <Col md="4">
          <div className="mb-3 top-label">
            <CodeSelect
              codes={[
                { value: "1", description: "1 - One" },
                { value: "2", description: "2 - Two" },
                { value: "3", description: "3 - Three" },
                { value: "4", description: "4 - Four" },
                { value: "5", description: "5 - Five" },
                { value: "6", description: "6 - Six" },
                { value: "7", description: "7 - Seven" },
                { value: "8", description: "8 - Eight" },
                { value: "9", description: "9 - Nine" },
                { value: "10", description: "10 - Ten" },
                { value: "11", description: "11 - Eleven" },
                { value: "12", description: "12 - Twelve" },
                { value: "13", description: "13 - Thirteen" },
                { value: "14", description: "14 - Fourteen" },
                { value: "15", description: "15 - Fifteen" },
                { value: "16", description: "16 - Sixteen" },
                { value: "17", description: "17 - Seventeen" },
                { value: "18", description: "18 - Eighteen" },
                { value: "19", description: "19 - Nineteen" },
                { value: "20", description: "20+ - Twenty or more" },
              ]}
              value={String(marriagesState)}
              onChange={(e) => setMarriagesState(String(e?.value ?? "1"))}
              placeholder=""
              name="marriages"
            />
            {renderLabel("# OF MARRIAGES (INCL. THIS ONE)", "marriages")}
          </div>
        </Col>
        <Col md="4">
          <Form.Check
            type="switch"
            id={`pregnant-${caption}`}
            label="Is this party currently pregnant?"
            checked={pregnantState}
            style={{ marginTop: "16px", marginBottom: "30px" }}
            onChange={(e) => setPregnantState(e.target.checked)}
          />
        </Col>
        {pregnantState ? (
          <Col md="4">
            <div className="mb-3 top-label">
              <DatePicker
                isClearable
                id={`dueDate-${caption}`}
                name="dueDate"
                className="form-control"
                selected={dueDateState ? new Date(dueDateState) : null}
                onChange={(date) => setDueDateState(date)}
              />
              {renderLabel("DUE DATE", "dueDate")}
            </div>
          </Col>
        ) : null}
      </Row>

      <Row className="g-3">
        <Col md="4">
          <div className="mb-3 top-label">
            <CodeSelect
              codes={[
                { value: "unknown", description: "Unknown / Prefer not to say" },
                { value: "none", description: "Not currently serving" },
                { value: "active", description: "Active Duty" },
                { value: "reserve", description: "Reserve / National Guard" },
              ]}
              value={militaryState}
              onChange={(e) => setMilitaryState(e?.value ?? "unknown")}
              placeholder=""
              name="military"
            />
            {renderLabel("MILITARY STATUS", "military")}
          </div>
        </Col>
      </Row>

      <Row className="g-3">
        <Col lg="12">
          <h2 className="small-title">Born At</h2>
        </Col>
      </Row>
      <Address
        {...(personState?.Birthplace || {})}
        name="Birthplace"
        id={`Birthplace-${caption}`}
        suggestions={getByPath(suggestions || {}, "Person.Birthplace") || {}}
        onApplySuggestion={(path, value) => applyNestedSuggestion("Person.Birthplace", path, value)}
        onRejectSuggestion={(path, value) => rejectNestedSuggestion("Person.Birthplace", path, value)}
        onChange={(values) =>
          updatePerson({
            Birthplace: { ...(values || {}) },
          })
        }
        simple
      />

      <Row className="g-3">
        <Col lg="12">
          <h2 className="small-title">Currently Resides At (Address)</h2>
        </Col>
      </Row>
      <Address
        {...(personState?.Residence || {})}
        name="Residence"
        id={`Residence-${caption}`}
        suggestions={getByPath(suggestions || {}, "Person.Residence") || {}}
        onApplySuggestion={(path, value) => applyNestedSuggestion("Person.Residence", path, value)}
        onRejectSuggestion={(path, value) => rejectNestedSuggestion("Person.Residence", path, value)}
        onChange={(values) =>
          updatePerson({
            Residence: { ...(values || {}) },
          })
        }
      />

      <Row className="g-3">
        <Col lg="12">
          <h2 className="small-title">Current Employer Information</h2>
        </Col>
      </Row>

      <Row className="g-3">
        <Col md="6" lg="8">
          <div className="mb-3 top-label">
            <Form.Control
              type="text"
              name="employerName"
              value={employerNameState}
              onChange={(e) => setEmployerNameState(e.target.value)}
            />
            {renderLabel("COMPANY NAME", "employerName")}
          </div>
        </Col>
        <Col md="6" lg="4">
          <div className="phone-bootstrap-wrap top-label">
            <PhoneInput
              name="employerPhone"
              id={`employerPhone-${caption}`}
              defaultCountry={defaultCountry ?? "us"}
              value={employerPhoneState}
              onChange={(value) => setEmployerPhoneState(value ?? "")}
              inputStyle={{
                width: "100%",
                height: "calc(1.5em + .75rem + 16px)",
                border: "1px solid #ced4da",
                borderRadius: "0 .375rem .375rem 0",
                padding: ".375rem .75rem",
                fontSize: "1rem",
                lineHeight: "1.5",
              }}
              countrySelectorStyleProps={{
                buttonStyle: {
                  border: "1px solid #ced4da",
                  borderRight: 0,
                  borderRadius: ".375rem 0 0 .375rem",
                  backgroundColor: "#fff",
                  height: "calc(1.5em + .75rem + 19px)",
                },
              }}
            />
            {renderLabel(
              "PHONE #",
              "employerPhone",
              { left: "calc(100% - 60px)", position: "absolute", backgroundColor: "transparent" }
            )}
          </div>
        </Col>
      </Row>

      <Address
        {...employerAddressState}
        name="EmployerAddress"
        id={`EmployerAddress-${caption}`}
        suggestions={getByPath(suggestions || {}, "EmployerAddress") || {}}
        onApplySuggestion={(path, value) => applyNestedSuggestion("EmployerAddress", path, value)}
        onRejectSuggestion={(path, value) => rejectNestedSuggestion("EmployerAddress", path, value)}
        onChange={(values) => setEmployerAddressState(values ?? {})}
      />

      <Overlay
        target={suggestionMenu.target}
        show={suggestionMenu.show}
        placement="bottom-end"
        rootClose
        onHide={closeSuggestionMenu}
      >
        {(overlayProps) => (
          <Popover {...overlayProps}>
            <Popover.Header as="h3">Supplied response</Popover.Header>
            <Popover.Body>
              <div className="small text-muted mb-2">{suggestionMenu.path}</div>
              <div className="mb-3" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {formatSuggestionValue(activeSuggestionValue, suggestionMenu.path)}
              </div>
              <div className="d-flex gap-2">
                <Button size="sm" variant="primary" onClick={applySuggestion}>
                  Accept
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={rejectSuggestion}>
                  Reject
                </Button>
              </div>
            </Popover.Body>
          </Popover>
        )}
      </Overlay>
    </Col>
  );
}

Party.propTypes = {
  uuid: PropTypes.string,
  auth: PropTypes.object,
  caption: PropTypes.string,
  Person: PropTypes.object,
  EmployerAddress: PropTypes.object,
  restore: PropTypes.bool,
  maiden: PropTypes.string,
  pregnant: PropTypes.bool,
  dueDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  marriages: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  military: PropTypes.string,
  title: PropTypes.string,
  employerName: PropTypes.string,
  employerPhone: PropTypes.string,
  defaultCountry: PropTypes.string,
  driversLicenseNumber: PropTypes.string,
  driversLicenseState: PropTypes.string,
  education: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  suggestions: PropTypes.object,
  onApplySuggestion: PropTypes.func,
  onRejectSuggestion: PropTypes.func,
};

Party.defaultProps = {
  onChange: null,
  caption: "Party",
  Person: {},
  EmployerAddress: {},
  suggestions: {},
  onApplySuggestion: null,
  onRejectSuggestion: null,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default withNavigation(connect(mapStateToProps, null, null, { forwardRef: true })(Party));
